import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  Info,
  ArrowRight,
  Home,
  Settings,
  AlertTriangle,
  Calculator as CalcIcon,
  Shield,
  Wrench,
} from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

export default function RoofingCostGuideSaskatchewan() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.saskroofingcompany.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Saskatchewan Roofing Cost Guide",
          item: "https://www.saskroofingcompany.com/roofing-cost-guide-saskatchewan",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id":
        "https://www.saskroofingcompany.com/roofing-cost-guide-saskatchewan#webpage",
      url: "https://www.saskroofingcompany.com/roofing-cost-guide-saskatchewan",
      name: "How Much Does a Roof Cost in Saskatchewan? (2026 Pricing Guide)",
      mainEntity: {
        "@type": "Article",
        headline:
          "How Much Does a Roof Cost in Saskatchewan? (2026 Pricing Guide)",
        author: { "@id": "https://www.saskroofingcompany.com/#organization" },
        publisher: {
          "@id": "https://www.saskroofingcompany.com/#organization",
        },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How much does a roof replacement cost in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Most homeowners in Saskatchewan pay between $6,000 and $15,000 for a standard asphalt shingle roof replacement on an average-sized home. Metal roofs typically cost between $15,000 and $30,000+.",
          },
        },
        {
          "@type": "Question",
          name: "Is a metal roof worth the extra cost in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, if you plan to stay in your home for 20+ years. Metal roofs last 50+ years, shed snow easily to prevent ice damming, and are highly resistant to hail damage, which can save you money on insurance and replacements over time.",
          },
        },
        {
          "@type": "Question",
          name: "Does home insurance cover roof replacement in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Insurance typically covers roof replacement if the damage is caused by a sudden, covered event like a hailstorm or severe windstorm, minus your deductible. It does not cover replacement due to normal wear and tear or aging.",
          },
        },
        {
          "@type": "Question",
          name: "How long does a roof last in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "A high-quality architectural asphalt shingle roof typically lasts 20 to 30 years in Saskatchewan's climate. Metal roofs can last 50 years or more with proper installation and minimal maintenance.",
          },
        },
        {
          "@type": "Question",
          name: "What is the cheapest roofing option in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "3-tab asphalt shingles are generally the cheapest upfront material, but architectural asphalt shingles offer a much better return on investment due to their longer lifespan and better wind resistance, making them the most cost-effective choice overall.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="Roofing Cost Guide Saskatchewan | Sask Roofing Company"
        description="Find out exactly how much a roof replacement costs in Saskatchewan. Explore pricing for asphalt, metal, and repairs in Saskatoon, Regina, and province-wide."
        canonical="/roofing-cost-guide-saskatchewan"
        schemaJson={schema}
      />

      {/* Hero */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a064f76a-cb98-4f5b-8cb3-4a80f8281705.png&output=webp&q=60"
            alt="Aerial view of a newly-roofed Saskatchewan home"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10 text-center">
          <div className="max-w-4xl mx-auto space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              How Much Does a Roof Cost in Saskatchewan? (2026 Pricing Guide)
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium max-w-2xl mx-auto">
              The complete, honest guide to roofing costs in Saskatoon, Regina,
              and across the province. No hidden fees, just straight answers.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="container px-4 max-w-4xl mx-auto">
          {/* TL;DR Box */}
          <div className="bg-primary/5 border-2 border-primary/20 rounded-2xl p-6 md:p-8 mb-12 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -mr-16 -mt-16"></div>
            <h2 className="text-2xl font-bold mb-4 text-foreground flex items-center gap-2">
              <Info className="w-6 h-6 text-primary" />
              The Short Answer (TL;DR)
            </h2>
            <p className="text-lg md:text-xl font-medium text-foreground leading-relaxed">
              Most Saskatchewan homeowners pay between{" "}
              <strong className="text-primary">$6,000 and $15,000</strong> for a
              standard asphalt shingle roof replacement. Metal roofs typically
              range from{" "}
              <strong className="text-primary">$15,000 to $30,000+</strong>.
              Minor to major roof repairs run between{" "}
              <strong className="text-primary">$300 and $3,000</strong>. For a
              personalized estimate based on your exact home, use our{" "}
              <Link
                to="/calculator"
                className="text-accent underline hover:text-accent/80"
              >
                free cost calculator
              </Link>
              .
            </p>
          </div>

          {/* Stat-callout grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-background border rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <Home className="w-8 h-8 mx-auto text-primary mb-4" />
              <div className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                Asphalt Shingle
              </div>
              <div className="text-2xl font-bold text-foreground">
                $6K - $15K
              </div>
              <div className="text-sm text-muted-foreground mt-2">
                Average Replacement
              </div>
            </div>
            <div className="bg-background border rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <Shield className="w-8 h-8 mx-auto text-primary mb-4" />
              <div className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                Metal Roofing
              </div>
              <div className="text-2xl font-bold text-foreground">
                $15K - $30K+
              </div>
              <div className="text-sm text-muted-foreground mt-2">
                Average Replacement
              </div>
            </div>
            <div className="bg-background border rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <Wrench className="w-8 h-8 mx-auto text-primary mb-4" />
              <div className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                Roof Repairs
              </div>
              <div className="text-2xl font-bold text-foreground">
                $300 - $3K
              </div>
              <div className="text-sm text-muted-foreground mt-2">
                Minor to Major Fixes
              </div>
            </div>
          </div>

          <div className="prose prose-lg max-w-none prose-headings:font-bold prose-a:text-accent hover:prose-a:text-accent/80">
            <p>
              Replacing a roof is one of the most significant investments you
              will make in your home. It protects everything else you own. In
              Saskatchewan, where we face extreme temperature swings, heavy snow
              loads, and summer hailstorms, choosing the right roof—and
              understanding what you are paying for—is critical.
            </p>
            <p>
              This guide breaks down exactly what you can expect to pay for
              roofing services across the province in 2026, what factors drive
              those costs up or down, and how to ensure you are getting real
              value for your money.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              Asphalt Shingle Roofing Cost Breakdown
            </h2>

            <div className="bg-muted/30 border p-6 rounded-xl mb-6">
              <h3 className="font-bold text-xl mb-2 text-foreground m-0">
                Estimated Cost: $6,000 – $15,000
              </h3>
              <p className="text-sm text-muted-foreground italic m-0">
                *This is a rough estimate for an average-sized home. Your final
                cost depends on size, pitch, and materials.
              </p>
            </div>

            <p>
              Asphalt shingles are the most popular roofing material in
              Saskatchewan, offering an excellent balance of affordability,
              durability, and aesthetic appeal. When looking at asphalt roofing
              costs, it is important to understand that not all shingles are
              created equal.
            </p>
            <p>
              <strong>What affects the price:</strong> The primary drivers are
              the size of your roof (measured in "squares," where one square
              equals 100 square feet), the steepness of the pitch, the
              complexity of the roof design (valleys, dormers, skylights), and
              the grade of shingle you choose.
            </p>
            <p>
              <strong>Architectural vs. 3-Tab:</strong> While traditional 3-tab
              shingles are cheaper upfront, architectural (or dimensional)
              shingles are far superior for Saskatchewan's climate. They are
              thicker, offer better wind resistance (often rated up to 130
              km/h), and provide a more textured, premium look. We almost
              exclusively recommend architectural shingles because the slight
              increase in upfront cost is vastly outweighed by the longer
              lifespan and better protection.
            </p>
            <p>
              <strong>Expected lifespan and warranty:</strong> A quality
              architectural shingle roof in Saskatchewan should last 20 to 30
              years. Manufacturer warranties typically range from 25 to 50
              years, and a reputable contractor will also provide a workmanship
              warranty of 5 to 10 years.
            </p>

            <figure className="my-8">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/2de89809-eea4-46a6-aaf9-fe65daf5aa25.png&output=webp&q=60"
                alt="Completed architectural asphalt shingle roof on a Saskatchewan home"
                className="rounded-xl shadow-md w-full h-auto object-cover max-h-96"
              />
              <figcaption className="text-center text-sm text-muted-foreground mt-2">
                A high-quality architectural shingle roof offers excellent
                protection and curb appeal.
              </figcaption>
            </figure>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              Metal Roofing Cost Breakdown
            </h2>

            <div className="bg-muted/30 border p-6 rounded-xl mb-6">
              <h3 className="font-bold text-xl mb-2 text-foreground m-0">
                Estimated Cost: $15,000 – $30,000+
              </h3>
              <p className="text-sm text-muted-foreground italic m-0">
                *This is a rough estimate for an average-sized home. Standing
                seam metal is a premium investment.
              </p>
            </div>

            <p>
              Metal roofing is a premium option that is growing in popularity
              across the prairies due to its incredible longevity and resilience
              against severe weather. While the initial investment is higher,
              the lifetime value is exceptional.
            </p>
            <p>
              <strong>Standing Seam vs. Exposed Fastener:</strong> The two main
              types of residential metal roofing have significantly different
              price points. Exposed fastener systems (often seen on agricultural
              buildings) are cheaper but require more maintenance over time as
              the rubber washers on the screws can degrade. Standing seam metal
              roofs, where the fasteners are hidden beneath the interlocking
              panels, are more expensive but offer superior weather-tightness
              and a sleek, modern aesthetic.
            </p>
            <p>
              <strong>Per-square-foot comparison:</strong> Metal roofing
              generally costs two to three times more per square foot than
              asphalt shingles, factoring in both the materials and the highly
              specialized labor required for proper installation.
            </p>
            <p>
              <strong>Expected lifespan:</strong> A standing seam metal roof can
              last 50 years or more. It sheds snow easily (preventing ice dams)
              and is highly resistant to hail, which is a major benefit in
              Saskatchewan. If you plan to stay in your home for decades, a
              metal roof can actually be cheaper over the long run because you
              won't need to replace it again.
            </p>

            <figure className="my-8">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/586cf018-038c-449e-b983-294747eb1d27.png&output=webp&q=60"
                alt="Completed standing seam metal roof installation"
                className="rounded-xl shadow-md w-full h-auto object-cover max-h-96"
              />
              <figcaption className="text-center text-sm text-muted-foreground mt-2">
                Standing seam metal roofs provide superior longevity and a
                modern aesthetic.
              </figcaption>
            </figure>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              Other Roofing Service Costs
            </h2>
            <p>
              Not every roofing project is a full replacement. Here is what you
              can expect to pay for other common services:
            </p>
            <ul>
              <li>
                <strong>Minor Repairs ($300 - $800):</strong> This covers simple
                fixes like replacing a few blown-off shingles, sealing minor
                flashing leaks around a vent, or patching a small puncture.
              </li>
              <li>
                <strong>Major Repairs ($1,000 - $3,000):</strong> This includes
                more extensive work, such as rebuilding a chimney flashing
                system, repairing a large section of storm damage, or fixing
                significant structural rot in a localized area.
              </li>
              <li>
                <strong>Eavestrough & Gutters ($1,200 - $2,500):</strong>{" "}
                Replacing the seamless gutters on an average-sized home
                typically falls in this range, depending on the linear footage
                and whether leaf guards are included.
              </li>
              <li>
                <strong>Roof Inspections:</strong> We provide free inspections
                for homeowners considering a repair or replacement. For
                specialized real estate transactions or detailed insurance
                reports, professional inspections typically run $150 to $300.
              </li>
              <li>
                <strong>Commercial Flat Roofing ($8 - $16 per sq. ft.):</strong>{" "}
                Commercial TPO or EPDM systems are priced per square foot, with
                costs varying heavily based on the amount of rigid insulation
                required to achieve proper drainage.
              </li>
            </ul>

            {/* Comparison Table 1 */}
            <div className="my-10 overflow-x-auto rounded-xl border bg-background shadow-sm">
              <h3 className="text-xl font-bold p-4 bg-muted/30 border-b m-0">
                Asphalt vs. Metal vs. Repair Comparison
              </h3>
              <table className="w-full text-left border-collapse m-0">
                <thead>
                  <tr className="bg-primary text-primary-foreground">
                    <th className="p-4 font-bold border-b border-primary-foreground/20">
                      Factor
                    </th>
                    <th className="p-4 font-bold border-b border-primary-foreground/20">
                      Asphalt Replacement
                    </th>
                    <th className="p-4 font-bold border-b border-primary-foreground/20">
                      Metal Replacement
                    </th>
                    <th className="p-4 font-bold border-b border-primary-foreground/20">
                      Roof Repair
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-medium text-foreground">
                      Average Cost
                    </td>
                    <td className="p-4 text-muted-foreground">
                      $6,000 - $15,000
                    </td>
                    <td className="p-4 text-muted-foreground">
                      $15,000 - $30,000+
                    </td>
                    <td className="p-4 text-muted-foreground">$300 - $3,000</td>
                  </tr>
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-medium text-foreground">
                      Lifespan
                    </td>
                    <td className="p-4 text-muted-foreground">20-30 Years</td>
                    <td className="p-4 text-muted-foreground">50+ Years</td>
                    <td className="p-4 text-muted-foreground">
                      Varies (Extends current roof)
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-medium text-foreground">
                      Climate Fit (SK)
                    </td>
                    <td className="p-4 text-muted-foreground">
                      Excellent (Architectural)
                    </td>
                    <td className="p-4 text-muted-foreground">
                      Superior (Hail/Snow)
                    </td>
                    <td className="p-4 text-muted-foreground">
                      Depends on existing roof
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-medium text-foreground">
                      Maintenance
                    </td>
                    <td className="p-4 text-muted-foreground">Low</td>
                    <td className="p-4 text-muted-foreground">Very Low</td>
                    <td className="p-4 text-muted-foreground">
                      Ongoing monitoring needed
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Comparison Table 2 */}
            <div className="my-10 overflow-x-auto rounded-xl border bg-background shadow-sm">
              <h3 className="text-xl font-bold p-4 bg-muted/30 border-b m-0">
                Estimated Cost by Roof Size (Asphalt)
              </h3>
              <table className="w-full text-left border-collapse m-0">
                <thead>
                  <tr className="bg-primary text-primary-foreground">
                    <th className="p-4 font-bold border-b border-primary-foreground/20">
                      Home Size (Sq. Ft.)
                    </th>
                    <th className="p-4 font-bold border-b border-primary-foreground/20">
                      Estimated Roof Size (Squares)
                    </th>
                    <th className="p-4 font-bold border-b border-primary-foreground/20">
                      Estimated Cost Range
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-medium text-foreground">
                      Small (1,000 - 1,500)
                    </td>
                    <td className="p-4 text-muted-foreground">
                      15 - 22 Squares
                    </td>
                    <td className="p-4 text-muted-foreground">
                      $5,500 - $8,500
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-medium text-foreground">
                      Medium (1,500 - 2,200)
                    </td>
                    <td className="p-4 text-muted-foreground">
                      22 - 32 Squares
                    </td>
                    <td className="p-4 text-muted-foreground">
                      $8,500 - $12,500
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-medium text-foreground">
                      Large (2,200 - 3,500+)
                    </td>
                    <td className="p-4 text-muted-foreground">
                      32 - 45+ Squares
                    </td>
                    <td className="p-4 text-muted-foreground">
                      $12,500 - $18,000+
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              What's Included in a Professional Quote
            </h2>
            <p>
              When comparing quotes, the lowest price isn't always the best
              deal. Often, lowball quotes leave out crucial components that will
              cost you later. A professional, fixed-price quote from Sask
              Roofing Company includes:
            </p>

            {/* Comparison Table 3 */}
            <div className="my-8 overflow-x-auto rounded-xl border bg-background shadow-sm">
              <table className="w-full text-left border-collapse m-0">
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-bold text-foreground w-1/3">
                      Tear-Off & Disposal
                    </td>
                    <td className="p-4 text-muted-foreground">
                      Complete removal of old shingles. We never shingle over an
                      old roof, as it hides rot and voids warranties. Bin rental
                      and disposal fees are included.
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-bold text-foreground">
                      Underlayment & Shield
                    </td>
                    <td className="p-4 text-muted-foreground">
                      Installation of synthetic underlayment and high-quality
                      ice & water shield along all eaves and valleys to prevent
                      ice damming leaks.
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-bold text-foreground">
                      Premium Materials
                    </td>
                    <td className="p-4 text-muted-foreground">
                      The shingles, ridge caps, starter strips, and all
                      necessary metal flashings (drip edge, step flashing,
                      chimney flashing).
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-bold text-foreground">
                      Ventilation Upgrades
                    </td>
                    <td className="p-4 text-muted-foreground">
                      Ensuring your attic has the correct balance of intake and
                      exhaust ventilation to meet building codes and
                      manufacturer specs.
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-bold text-foreground">
                      Labor & Cleanup
                    </td>
                    <td className="p-4 text-muted-foreground">
                      Expert installation by our local crews, followed by a
                      thorough property cleanup and magnetic sweep for stray
                      nails.
                    </td>
                  </tr>
                  <tr className="hover:bg-muted/50 transition-colors">
                    <td className="p-4 font-bold text-foreground">
                      Permits & Warranties
                    </td>
                    <td className="p-4 text-muted-foreground">
                      Any necessary municipal permits, plus written
                      documentation for both workmanship and manufacturer
                      warranties.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-accent/10 border-l-4 border-accent p-6 rounded-r-xl my-10">
              <h3 className="text-xl font-bold mt-0 mb-2 text-foreground flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-accent" />
                The Cost Factors That Matter Most
              </h3>
              <p className="m-0 text-muted-foreground">
                <strong>
                  Roof pitch and decking condition drive 70% of the cost
                  variance
                </strong>{" "}
                between two homes of the exact same size. A steep roof requires
                specialized safety equipment and takes longer to install. Rotted
                decking hidden under old shingles must be replaced before new
                materials can go down. Get a professional inspection first so
                you know exactly what you're dealing with.
              </p>
            </div>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              7 Factors That Affect Roof Pricing in Saskatchewan
            </h2>
            <p>
              Every home is unique. Here are the specific variables that dictate
              the final price of your project:
            </p>

            <ol className="space-y-6 my-8 pl-0 list-none">
              <li className="flex gap-4 items-start">
                <span className="flex-shrink-0 w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-lg">
                  1
                </span>
                <div>
                  <h3 className="text-xl font-bold m-0 mb-1">
                    Roof Size (Square Footage)
                  </h3>
                  <p className="m-0 text-muted-foreground">
                    The most obvious factor. Roofing materials are sold by the
                    "square" (100 sq. ft.). The larger the roof, the more
                    materials and labor required.
                  </p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="flex-shrink-0 w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-lg">
                  2
                </span>
                <div>
                  <h3 className="text-xl font-bold m-0 mb-1">
                    Roof Pitch (Steepness)
                  </h3>
                  <p className="m-0 text-muted-foreground">
                    A walkable roof (e.g., 4/12 pitch) is faster and cheaper to
                    roof than a steep, non-walkable roof (e.g., 10/12 pitch)
                    which requires roof jacks, harnesses, and significantly more
                    time.
                  </p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="flex-shrink-0 w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-lg">
                  3
                </span>
                <div>
                  <h3 className="text-xl font-bold m-0 mb-1">
                    Complexity and Penetrations
                  </h3>
                  <p className="m-0 text-muted-foreground">
                    A simple gable roof is straightforward. A roof with multiple
                    dormers, valleys, skylights, and chimneys requires intricate
                    flashing work and custom cutting, which increases labor
                    costs.
                  </p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="flex-shrink-0 w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-lg">
                  4
                </span>
                <div>
                  <h3 className="text-xl font-bold m-0 mb-1">
                    Material Choice
                  </h3>
                  <p className="m-0 text-muted-foreground">
                    As discussed, upgrading from standard architectural shingles
                    to Class 4 impact-resistant shingles or standing seam metal
                    will increase the upfront cost.
                  </p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="flex-shrink-0 w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-lg">
                  5
                </span>
                <div>
                  <h3 className="text-xl font-bold m-0 mb-1">
                    Condition of the Decking
                  </h3>
                  <p className="m-0 text-muted-foreground">
                    If the plywood decking beneath your old shingles is rotted
                    from years of leaks or poor ventilation, it must be replaced
                    (typically charged per sheet of plywood).
                  </p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="flex-shrink-0 w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-lg">
                  6
                </span>
                <div>
                  <h3 className="text-xl font-bold m-0 mb-1">Accessibility</h3>
                  <p className="m-0 text-muted-foreground">
                    If our trucks and dumpsters can easily back up to your
                    house, the job goes faster. If your home is tightly packed
                    between neighbors with difficult access, material handling
                    takes longer.
                  </p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="flex-shrink-0 w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-lg">
                  7
                </span>
                <div>
                  <h3 className="text-xl font-bold m-0 mb-1">
                    Number of Existing Layers
                  </h3>
                  <p className="m-0 text-muted-foreground">
                    Tearing off one layer of old shingles is standard. If a
                    previous contractor shingled over an old roof, tearing off
                    two or three layers doubles the labor and disposal fees.
                  </p>
                </div>
              </li>
            </ol>

            <figure className="my-8">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/65e89659-c290-449e-b7d6-c114382c4ccf.png&output=webp&q=60"
                alt="Roofing crew working on a steep pitch roof in Saskatchewan"
                className="rounded-xl shadow-md w-full h-auto object-cover max-h-96"
              />
              <figcaption className="text-center text-sm text-muted-foreground mt-2">
                Steep pitches and complex roof designs require specialized
                safety equipment and increase labor time.
              </figcaption>
            </figure>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              How to Save Money on Your Roof Replacement
            </h2>
            <p>
              While you should never cut corners on materials or hire an
              uninsured "guy in a truck," there are legitimate ways to manage
              the cost of a new roof:
            </p>
            <ul>
              <li>
                <strong>Plan Ahead:</strong> Emergency repairs and winter
                installations carry a premium. By getting an inspection before
                your roof fails, you can schedule the work during the standard
                season.
              </li>
              <li>
                <strong>Check Your Insurance:</strong> If your roof was damaged
                by a recent hailstorm or severe wind event, your homeowner's
                insurance may cover the replacement cost (minus your
                deductible). We can help document the damage for your claim.
              </li>
              <li>
                <strong>Invest in Quality Upfront:</strong> Spending 10% more on
                better underlayment, proper ventilation, and architectural
                shingles can prevent thousands of dollars in premature failure
                or ice damming damage down the road.
              </li>
              <li>
                <strong>Explore Financing:</strong> We offer flexible{" "}
                <Link to="/financing">financing options</Link> to help you break
                a large upfront cost into manageable monthly payments.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-muted/30 border-y">
        <div className="container px-4 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Frequently Asked Questions About Roofing Costs
          </h2>
          <Accordion
            type="single"
            collapsible
            className="w-full bg-background rounded-xl border p-2 shadow-sm"
          >
            {(schema[2] as any).mainEntity.map((faq: any, i: number) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b-0 px-4"
              >
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  {faq.name}
                </AccordionTrigger>
                <AccordionContent
                  forceMount={true}
                  className="text-muted-foreground text-base pb-6 leading-relaxed data-[state=closed]:h-0 data-[state=closed]:overflow-hidden"
                >
                  {faq.acceptedAnswer.text}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Cross Links & CTA Banner */}
      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-4xl mx-auto space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">
            Get an Exact Quote for Your Roof
          </h2>
          <p className="text-xl text-primary-foreground/90 leading-relaxed">
            Stop guessing. Get a free, no-obligation inspection and a
            fixed-price written quote for your specific home. We serve
            Saskatoon, Regina, Moose Jaw, Prince Albert, Swift Current, Yorkton,
            Estevan, Weyburn, and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
              asChild
            >
              <Link to="/contact">Book a Free Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 h-auto bg-transparent border-white text-white hover:bg-white/10"
              asChild
            >
              <Link to="/calculator" className="flex items-center gap-2">
                <CalcIcon className="w-5 h-5" />
                Try the Cost Calculator
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
